// screens/marketplace/PreOrderCampaigns.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clock,
  Package,
  CalendarDays,
  ChevronRight,
  RefreshCw,
  ArrowRight,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useTheme } from "../../hooks/useTheme";
import { useToast } from "../../providers/ToastProvider";
import StorefrontHeader from "../../components/StorefrontHeader";

const RECENT_WINDOW_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
const DAY_MS = 86400000;

const formatShortDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });

const formatRange = (startIso, endIso) =>
  `${formatShortDate(startIso)} → ${formatShortDate(endIso)}`;

const daysBetween = (a, b) => Math.ceil((b - a) / DAY_MS);

const campaignPhase = (c, now) => {
  const start = new Date(c.start_date).getTime();
  const end = new Date(c.end_date).getTime();
  if (!c.is_active) return "closed";
  if (now < start) return "upcoming";
  if (now > end) return "closed";
  return "open";
};

const itemsOf = (c) => c.products || c.pre_order_products || [];

export default function PreOrderCampaigns() {
  const { authFetch } = useAuth();
  const { colors } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [now, setNow] = useState(Date.now());

  // Live tick — refresh once a minute
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(
    async ({ silent } = {}) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const res = await authFetch("/marketplace/pre-orders/");
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Could not load campaigns");
        setCampaigns(data.data || []);
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authFetch, showToast]
  );

  useEffect(() => {
    load();
  }, [load]);

  // ── Split into three groups ────────────────────────────
  const { open, upcoming, recentClosed } = useMemo(() => {
    const open = [];
    const upcoming = [];
    const recentClosed = [];

    campaigns.forEach((c) => {
      const phase = campaignPhase(c, now);
      if (phase === "open") open.push(c);
      else if (phase === "upcoming") upcoming.push(c);
      else {
        const endTime = new Date(c.end_date).getTime();
        if (now - endTime <= RECENT_WINDOW_MS) recentClosed.push(c);
      }
    });

    open.sort((a, b) => new Date(a.end_date) - new Date(b.end_date));
    upcoming.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    recentClosed.sort((a, b) => new Date(b.end_date) - new Date(a.end_date));

    return { open, upcoming, recentClosed };
  }, [campaigns, now]);

  // ── Stats for the top strip ────────────────────────────
  const stats = useMemo(() => {
    const totalProductsInOpen = open.reduce(
      (sum, c) => sum + (c.total_products ?? itemsOf(c).length),
      0
    );
    const nextClose = open.length
      ? Math.max(
        0,
        daysBetween(now, new Date(open[0].end_date).getTime())
      )
      : null;
    return { totalProductsInOpen, nextClose };
  }, [open, now]);

  const hasAnything = open.length || upcoming.length || recentClosed.length;

  return (
    <div style={styles.pageWrap}>
      <StorefrontHeader
        title="Pre-order windows"
        subtitle="Order during a window. Everything in it ships together once the window closes."
      />

      {/* Utility row: refresh + stat strip */}
      {!loading && hasAnything && (
        <div style={styles.utilityRow}>
          <StatStrip
            stats={stats}
            openCount={open.length}
            upcomingCount={upcoming.length}
            colors={colors}
          />
          <button
            type="button"
            onClick={() => load({ silent: true })}
            disabled={refreshing}
            style={styles.ghostBtn(colors)}
          >
            <RefreshCw
              size={13}
              style={{
                animation: refreshing ? "spin 1s linear infinite" : "none",
              }}
            />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      )}

      {loading ? (
        <SkeletonSections colors={colors} />
      ) : !hasAnything ? (
        <EmptyState colors={colors} onBrowse={() => navigate("/marketplace")} />
      ) : (
        <>
          {open.length > 0 && (
            <Section
              title="Open now"
              description="Accepting orders right now"
              count={open.length}
              tone="live"
              colors={colors}
            >
              <div style={styles.campaignGrid}>
                {open.map((c) => (
                  <CampaignCard
                    key={c.id}
                    campaign={c}
                    phase="open"
                    now={now}
                    colors={colors}
                    onOpen={() => navigate(`/preorder/campaigns/${c.id}`)}
                  />
                ))}
              </div>
            </Section>
          )}

          {upcoming.length > 0 && (
            <Section
              title="Opening soon"
              description="Set a reminder for these dates"
              count={upcoming.length}
              tone="soon"
              colors={colors}
            >
              <div style={styles.campaignGrid}>
                {upcoming.map((c) => (
                  <CampaignCard
                    key={c.id}
                    campaign={c}
                    phase="upcoming"
                    now={now}
                    colors={colors}
                    onOpen={() => navigate(`/preorder/campaigns/${c.id}`)}
                  />
                ))}
              </div>
            </Section>
          )}

          {recentClosed.length > 0 && (
            <Section
              title="Recently closed"
              description="From the last two weeks"
              count={recentClosed.length}
              tone="closed"
              colors={colors}
            >
              <div style={styles.campaignGrid}>
                {recentClosed.map((c) => (
                  <CampaignCard
                    key={c.id}
                    campaign={c}
                    phase="closed"
                    now={now}
                    colors={colors}
                    onOpen={() => navigate(`/preorder/campaigns/${c.id}`)}
                  />
                ))}
              </div>
            </Section>
          )}
        </>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Stat strip
   ══════════════════════════════════════════════════════════ */

const StatStrip = ({ stats, openCount, upcomingCount, colors }) => {
  const nextCloseLabel = (() => {
    if (stats.nextClose === null) return "—";
    if (stats.nextClose === 0) return "Today";
    if (stats.nextClose === 1) return "Tomorrow";
    return `${stats.nextClose} days`;
  })();

  return (
    <div style={styles.statStrip(colors)}>
      <StatItem
        icon={Clock}
        label="Open now"
        value={openCount}
        colors={colors}
      />
      <Divider colors={colors} />
      <StatItem
        icon={Package}
        label="Products inside"
        value={stats.totalProductsInOpen}
        colors={colors}
      />
      <Divider colors={colors} />
      <StatItem
        icon={CalendarDays}
        label="Next window closes"
        value={nextCloseLabel}
        colors={colors}
        highlight
      />
      {upcomingCount > 0 && (
        <>
          <Divider colors={colors} />
          <StatItem
            icon={ArrowRight}
            label="Coming up"
            value={upcomingCount}
            colors={colors}
          />
        </>
      )}
    </div>
  );
};

const StatItem = ({ icon: Icon, label, value, colors, highlight }) => (
  <div style={styles.statItem}>
    <span
      style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: highlight ? colors.warningBg : colors.hoverBg,
        color: highlight ? colors.warning : colors.btnBg,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon size={14} />
    </span>
    <div style={{ minWidth: 0 }}>
      <div style={styles.statValue(colors)}>{value}</div>
      <div style={styles.statLabel(colors)}>{label}</div>
    </div>
  </div>
);

const Divider = ({ colors }) => (
  <span
    style={{
      width: 1,
      height: 26,
      background: colors.separator || colors.border,
      flexShrink: 0,
    }}
  />
);

/* ══════════════════════════════════════════════════════════
   Section wrapper
   ══════════════════════════════════════════════════════════ */

const TONE = {
  live: { dot: "#16A34A" },
  soon: { dot: "#F59E0B" },
  closed: { dot: "#9CA3AF" },
};

const Section = ({ title, description, count, tone, colors, children }) => (
  <section style={{ marginBottom: 36 }}>
    <header style={styles.sectionHeader}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: TONE[tone].dot,
          flexShrink: 0,
        }}
      />
      <h2 style={styles.sectionTitle(colors)}>{title}</h2>
      <span style={styles.sectionChip(colors)}>{count}</span>
      <span style={styles.sectionDesc(colors)} className="po-section-desc">
        {description}
      </span>
    </header>
    {children}
    <style>{`
      @media (max-width: 640px) {
        .po-section-desc { display: none; }
      }
    `}</style>
  </section>
);

/* ══════════════════════════════════════════════════════════
   Campaign card
   ══════════════════════════════════════════════════════════ */

const PHASE_STYLE = {
  open: { strip: "#16A34A" },
  upcoming: { strip: "#F59E0B" },
  closed: { strip: "#9CA3AF" },
};

const CampaignCard = ({ campaign, phase, now, colors, onOpen }) => {
  const items = itemsOf(campaign);
  const total = campaign.total_products ?? items.length;
  const availableCount = items.filter(
    (i) =>
      i.is_available &&
      (i.stock_limit === 0 || i.sold_count < i.stock_limit)
  ).length;
  const soldCount = items.reduce((s, i) => s + (i.sold_count || 0), 0);

  const start = new Date(campaign.start_date).getTime();
  const end = new Date(campaign.end_date).getTime();

  // Time chip
  let timeLabel = "";
  let timeTone = "muted";
  if (phase === "open") {
    const d = Math.max(0, daysBetween(now, end));
    if (d === 0) {
      timeLabel = "Closing today";
      timeTone = "urgent";
    } else if (d <= 2) {
      timeLabel = `Closes in ${d} day${d === 1 ? "" : "s"}`;
      timeTone = "soon";
    } else {
      timeLabel = `${d} days left`;
      timeTone = "open";
    }
  } else if (phase === "upcoming") {
    const d = Math.max(0, daysBetween(now, start));
    timeLabel =
      d === 0 ? "Opens today" : d === 1 ? "Opens tomorrow" : `Opens in ${d} days`;
    timeTone = "soon";
  } else {
    const d = Math.max(0, daysBetween(end, now));
    timeLabel = d === 0 ? "Closed today" : `Closed ${d} day${d === 1 ? "" : "s"} ago`;
    timeTone = "muted";
  }

  const toneStyle = {
    muted: { fg: colors.secondaryText, bg: colors.hoverBg },
    soon: { fg: colors.warning, bg: colors.warningBg },
    urgent: { fg: colors.error, bg: colors.errorBg },
    open: { fg: colors.success, bg: colors.successBg },
  }[timeTone];

  const progress = total > 0 ? Math.round((availableCount / total) * 100) : 0;
  const strip = PHASE_STYLE[phase].strip;

  return (
    <button
      type="button"
      onClick={onOpen}
      style={styles.card(colors)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.borderColor = colors.btnBg;
        e.currentTarget.style.boxShadow = `0 12px 32px ${colors.shadow}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = colors.border;
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Phase strip on the left */}
      <span
        style={{
          position: "absolute",
          top: 14,
          bottom: 14,
          left: 0,
          width: 3,
          borderRadius: 3,
          background: strip,
          opacity: phase === "open" ? 1 : 0.55,
        }}
      />

      {/* Header: time chip + arrow */}
      <div style={styles.cardHeader}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 10px",
            borderRadius: 999,
            background: toneStyle.bg,
            color: toneStyle.fg,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.2,
          }}
        >
          <Clock size={11} />
          {timeLabel}
        </span>
        <ChevronRight size={16} color={colors.secondaryText} />
      </div>

      {/* Name + description */}
      <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        <h3 style={styles.cardTitle(colors)}>{campaign.name}</h3>
        {campaign.description && (
          <p style={styles.cardDesc(colors)}>{campaign.description}</p>
        )}
      </div>

      {/* Availability meter (only if there are products) */}
      {total > 0 && (
        <div style={styles.meter(colors)}>
          <div style={styles.meterBar(colors)}>
            <div
              style={{
                height: "100%",
                width: `${progress}%`,
                background: strip,
                borderRadius: 2,
                transition: "width 0.4s ease",
              }}
            />
          </div>
          <div style={styles.meterLabels(colors)}>
            <span>
              {availableCount} of {total} available
            </span>
            {soldCount > 0 && <span>{soldCount} sold</span>}
          </div>
        </div>
      )}

      {/* Footer: date range */}
      <div style={styles.cardFooter(colors)}>
        <CalendarDays size={12} />
        <span>{formatRange(campaign.start_date, campaign.end_date)}</span>
      </div>
    </button>
  );
};

/* ══════════════════════════════════════════════════════════
   Empty state
   ══════════════════════════════════════════════════════════ */

const EmptyState = ({ colors, onBrowse }) => (
  <div style={styles.emptyWrap(colors)}>
    <div style={styles.emptyIcon(colors)}>
      <Clock size={26} />
    </div>
    <h3 style={styles.emptyTitle(colors)}>No pre-order windows right now</h3>
    <p style={styles.emptyText(colors)}>
      New group-buy windows open regularly. Meanwhile, browse the marketplace
      for ready-to-ship items — those ship immediately.
    </p>
    <button type="button" onClick={onBrowse} style={styles.emptyBtn(colors)}>
      Browse marketplace
    </button>
  </div>
);

/* ══════════════════════════════════════════════════════════
   Loading skeleton
   ══════════════════════════════════════════════════════════ */

const SkeletonSections = ({ colors }) => (
  <div style={{ display: "grid", gap: 36 }}>
    {[0, 1].map((group) => (
      <div key={group}>
        <div
          style={{
            height: 16,
            width: 140,
            borderRadius: 4,
            background: colors.hoverBg,
            marginBottom: 16,
            animation: "po-skeleton 1.4s ease-in-out infinite",
          }}
        />
        <div style={styles.campaignGrid}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                height: 200,
                borderRadius: 16,
                background: colors.raisedBg,
                border: `1px solid ${colors.border}`,
                animation: "po-skeleton 1.4s ease-in-out infinite",
              }}
            />
          ))}
        </div>
      </div>
    ))}
    <style>{`
      @keyframes po-skeleton {
        0%, 100% { opacity: 0.7; }
        50% { opacity: 0.35; }
      }
    `}</style>
  </div>
);

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  pageWrap: {
    maxWidth: 1180,
    margin: "0 auto",
    padding: "20px 16px 60px",
  },

  // ── Utility row ─────────────────────────────────────
  utilityRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 24,
    flexWrap: "wrap",
  },
  statStrip: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "10px 16px",
    borderRadius: 12,
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    flex: "1 1 auto",
    minWidth: 0,
    overflowX: "auto",
    scrollbarWidth: "none",
  }),
  statItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexShrink: 0,
  },
  statValue: (c) => ({
    fontSize: 15,
    fontWeight: 800,
    color: c.heading,
    letterSpacing: "-0.01em",
    lineHeight: 1.15,
  }),
  statLabel: (c) => ({
    fontSize: 10.5,
    color: c.secondaryText,
    fontWeight: 600,
    letterSpacing: 0.2,
    textTransform: "uppercase",
    marginTop: 1,
  }),
  ghostBtn: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "10px 14px",
    borderRadius: 10,
    border: `1px solid ${c.border}`,
    background: "transparent",
    color: c.text,
    cursor: "pointer",
    fontSize: 12.5,
    fontWeight: 600,
    flexShrink: 0,
  }),

  // ── Section ─────────────────────────────────────────
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  sectionTitle: (c) => ({
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
    color: c.heading,
    letterSpacing: "-0.01em",
  }),
  sectionChip: (c) => ({
    fontSize: 11.5,
    fontWeight: 700,
    padding: "2px 9px",
    borderRadius: 999,
    background: c.hoverBg,
    color: c.secondaryText,
  }),
  sectionDesc: (c) => ({
    fontSize: 12.5,
    color: c.secondaryText,
    marginLeft: 4,
  }),

  campaignGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 16,
  },

  // ── Card ────────────────────────────────────────────
  card: (c) => ({
    position: "relative",
    textAlign: "left",
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    borderRadius: 16,
    padding: "18px 18px 16px 22px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    transition:
      "transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
    fontFamily: "inherit",
    minHeight: 220,
    overflow: "hidden",
  }),
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  cardTitle: (c) => ({
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
    color: c.heading,
    letterSpacing: "-0.01em",
    lineHeight: 1.3,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  }),
  cardDesc: (c) => ({
    margin: 0,
    fontSize: 12.5,
    color: c.secondaryText,
    lineHeight: 1.5,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  }),

  // ── Availability meter ──────────────────────────────
  meter: (c) => ({
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginTop: "auto",
  }),
  meterBar: (c) => ({
    height: 4,
    borderRadius: 2,
    background: c.hoverBg,
    overflow: "hidden",
  }),
  meterLabels: (c) => ({
    display: "flex",
    justifyContent: "space-between",
    fontSize: 11,
    color: c.secondaryText,
    fontWeight: 600,
  }),

  // ── Card footer ─────────────────────────────────────
  cardFooter: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 6,
    paddingTop: 12,
    borderTop: `1px solid ${c.separator || c.border}`,
    fontSize: 11.5,
    color: c.secondaryText,
  }),

  // ── Empty ───────────────────────────────────────────
  emptyWrap: (c) => ({
    textAlign: "center",
    padding: "60px 24px",
    borderRadius: 16,
    border: `1px dashed ${c.border}`,
    background: c.raisedBg,
  }),
  emptyIcon: (c) => ({
    width: 64,
    height: 64,
    borderRadius: 18,
    background: c.hoverBg,
    color: c.secondaryText,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  }),
  emptyTitle: (c) => ({
    margin: "0 0 6px",
    fontSize: 17,
    fontWeight: 800,
    color: c.heading,
  }),
  emptyText: (c) => ({
    margin: "0 auto 22px",
    maxWidth: 400,
    fontSize: 13.5,
    color: c.secondaryText,
    lineHeight: 1.55,
  }),
  emptyBtn: (c) => ({
    padding: "11px 20px",
    borderRadius: 10,
    border: "none",
    background: c.btnBg,
    color: c.btnText,
    fontSize: 13.5,
    fontWeight: 700,
    cursor: "pointer",
  }),
};